import React, { useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons/faInfoCircle";
import { faUsers } from "@fortawesome/free-solid-svg-icons/faUsers";
import { faCalendarAlt } from "@fortawesome/free-solid-svg-icons/faCalendarAlt";
import { faClock } from "@fortawesome/free-solid-svg-icons/faClock";
import ReactTooltip from "react-tooltip";
import ReactDOMServer from "react-dom/server";
import { round } from "lodash";
import { formatCommaZeroDp } from "../../utils/dataHelpers/formatters";
import { BlurbPtoAvg, H, BlurbTimeAvg } from "../Text";

const CountyOverview = ({ countyData, judgesData, year }) => {
  const { note } = countyData;
  const countyStats = useMemo(() => {
    const totalJudges = judgesData.length;
    const {
      label,
      avgNdwDays,
      avgDaysWithOnlyEventsBefore1Pm,
      noData,
    } = countyData;
    if (noData) {
      return [];
    }
    const formattedPto = formatCommaZeroDp(avgNdwDays);
    const ptoDayTextFrag = round(avgNdwDays, 0) === 1 ? "day" : "days";
    const formattedAfternoons = formatCommaZeroDp(
      avgDaysWithOnlyEventsBefore1Pm
    );
    const afternoonsTextFrag =
      round(avgDaysWithOnlyEventsBefore1Pm, 0) === 1 ? "day" : "days";
    return [
      {
        id: "judges",
        text: (
          <span>
            <H>{totalJudges}</H> district judges
          </span>
        ),
        icon: faUsers,
      },
      {
        id: "pto",
        text: (
          <span>
            Average of <H>{formattedPto}</H> {ptoDayTextFrag} without case events
          </span>
        ),
        icon: faCalendarAlt,
        info: (
          <BlurbPtoAvg countyName={label} avgPtoDays={avgNdwDays} year={year} />
        ),
      },
      {
        id: "afternoons",
        text: (
          <span>
            Average of <H>{formattedAfternoons}</H> {afternoonsTextFrag} with no
            afternoon proceedings
          </span>
        ),
        icon: faClock,
        info: (
          <BlurbTimeAvg
            countyName={label}
            avgNoAfternoonDays={avgDaysWithOnlyEventsBefore1Pm}
            year={year}
          />
        ),
      },
    ];
  }, [judgesData, countyData, year]);
  useEffect(() => {
    ReactTooltip.rebuild();
  }, []);

  return (
    <div className="panel__county-overview-container">
      <div className="panel__county-overview-stats-container">
        {countyStats.map(({ id, text, icon, info }) => (
          <StatsItem icon={icon} info={info} key={id}>
            {text}
          </StatsItem>
        ))}
      </div>
      {note && (
        <div className="panel__county-overview-footnote">Note: {note}</div>
      )}
    </div>
  );
};

CountyOverview.propTypes = {
  judgesData: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)).isRequired,
  countyData: PropTypes.objectOf(PropTypes.any).isRequired,
  year: PropTypes.number.isRequired,
};

const StatsItem = ({ icon, children, info }) => {
  return (
    <div className="panel__county-overview-stats-item-container">
      <div className="panel__county-overview-stats-item-icon">
        <FontAwesomeIcon icon={icon} size="2x" />
      </div>
      <div className="panel__county-overview-stats-item-text">
        <span>
          {children}
          {info && (
            <span>
              <FontAwesomeIcon
                className="panel__info-circle margin-left"
                data-tip={ReactDOMServer.renderToStaticMarkup(info)}
                data-for="tooltip__info-icon"
                icon={faInfoCircle}
              />
            </span>
          )}
        </span>
      </div>
    </div>
  );
};

StatsItem.defaultProps = {
  info: null,
};

StatsItem.propTypes = {
  icon: PropTypes.objectOf(PropTypes.any).isRequired,
  children: PropTypes.node.isRequired,
  info: PropTypes.node,
};

export default CountyOverview;
